import { ReduxState } from "ra-core";
import { FC, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Stack, Box, Input, Divider, CircularProgress } from "@mui/material";
import SearchIcon from '~/icons/Search';
import PopAutocompleteFilter from "../input/PopAutocompleteFilter";
import { useFileCataSearchContext } from "./FileCataSearchContextProvider";
import { useFileCataAmbientDataContext } from "./FileCataAmbientDataProvider";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { PrimaryClassification, SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { FileTagResponseDto } from "~/gen/api/securefile";
import SearchCriteriaChips, { ChipItem } from "../input/SearchCriteriaChips";


const FileCatalogSearchRegion: FC = () => {
  const loading = useSelector<ReduxState>(state => state.admin.loading > 0)
  const { classifications, tags } = useFileCataAmbientDataContext()
  const { 
    filters, 
    searchName, 
    addSecondClassification, 
    removeSecondClassification, 
    removePrimaryClassification, 
    addSearchTag, 
    removeSearchTag, 
  } = useFileCataSearchContext()

  const [inputFocused, setInputFocused] = useState(false)
  
  const criteriaChips = useMemo(() => {
    const arr = [] as ChipItem[][]
    if (filters.name) {
      arr.push([{
        id: 'name',
        label: 'Name',
        content: `like *${filters.name}*`,
        delete: () => searchName(null),
      }])
    }
    if (filters.primaryClassification.length > 0 || filters.secondClassification.length > 0) {
      arr.push([
        ...filters.primaryClassification.map(p => ({
          id: p,
          label: 'Classification',
          content: PrimaryClassification[p],
          delete: () => removePrimaryClassification(p),
        })),
        ...filters.secondClassification.map(s => ({
          id: s.secondaryClassificationId,
          label: 'Classification',
          content: (s as any).text,
          delete: () => removeSecondClassification(s),
        }))
      ])
    }
    if (filters.tags.length > 0) {
      arr.push(filters.tags.map(t => ({
        id: `t${t.tagId}`,
        label: 'Tag',
        content: t.name!,
        delete: () => removeSearchTag(t)
      })))
    }
    return arr
  }, [filters, removePrimaryClassification, removeSearchTag, removeSecondClassification, searchName])

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ p: 2 }}>
        {loading ? 
          <CircularProgress size={20} /> :
          <SearchIcon fontSize="small" />}
        <Box sx={{ flexGrow: 1, ml: 3 }}>
          <Input fullWidth placeholder="Search by Name" disableUnderline={true} 
            value={inputFocused ? filters.name : ''}
            onChange={(e) => searchName(e.target.value)}
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
          />
        </Box>
      </Stack>
      <Divider />
      <SearchCriteriaChips criteriaChips={criteriaChips} />
      <Divider />
      <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ p: 1 }} spacing={2}>
        <Box>
          <PopAutocompleteFilter
            label="Classification"
            AutocompleteProps={{
              id: 'classification-picker',
              options: classifications,
              getOptionLabel: (option) => option.text,
              renderOption: (props, option, { inputValue }) => {
                const matches = match(option.text, inputValue, {insideWords:true});
                const parts = parse(option.text, matches);

                return (
                  <li {...props}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </Box>
                  </li>
                );
              },
              onChange: (event, option) => {
                const clsItem = option as SecondaryClassificationResponseDto
                if (clsItem) {
                  addSecondClassification(clsItem)
                }
              },
            }}
          />
        </Box>
        <Box>
          <PopAutocompleteFilter
            label="Tag"
            AutocompleteProps={{
              id: 'tag-picker',
              options: tags,
              getOptionLabel: (option) => option.text,
              renderOption: (props, option, { inputValue }) => {
                const matches = match(option.text, inputValue, {insideWords:true});
                const parts = parse(option.text, matches);

                return (
                  <li {...props}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                      {parts.map((part, index) => (
                        <span
                          key={index}
                          style={{
                            fontWeight: part.highlight ? 700 : 400,
                          }}
                        >
                          {part.text}
                        </span>
                      ))}
                    </Box>
                  </li>
                );
              },
              onChange: (event, option) => {
                const tagItem = option as FileTagResponseDto
                if (tagItem) {
                  addSearchTag(tagItem)
                }
              },
            }}
          />
        </Box>
      </Stack>
    </>
  )
}

export default FileCatalogSearchRegion;

